import { useState, useCallback } from "react";
import { FloatingPanelState } from "../components/session/FloatingPanel";

export type PanelPreferences = Record<string, FloatingPanelState>;

const storageKey = (userId: string) => `rpg-panels-${userId}`;

function loadPreferences(userId: string): PanelPreferences {
  if (!userId) return {};
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (!raw) return {};
    return JSON.parse(raw) as PanelPreferences;
  } catch {
    return {};
  }
}

function savePreferences(userId: string, prefs: PanelPreferences): void {
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify(prefs));
  } catch {
    // Ignore storage quota errors
  }
}

/**
 * Persists floating-panel layout preferences to localStorage per user.
 *
 * @param userId  The authenticated user's ID — preferences are stored per user.
 * @returns       [preferences, updatePreference]
 */
export function usePanelPreferences(
  userId: string
): [PanelPreferences, (_panelId: string, _state: FloatingPanelState) => void] {
  const [preferences, setPreferences] = useState<PanelPreferences>(() => loadPreferences(userId));

  const updatePreference = useCallback(
    (panelId: string, state: FloatingPanelState) => {
      setPreferences((prev) => {
        const next = { ...prev, [panelId]: state };
        savePreferences(userId, next);
        return next;
      });
    },
    [userId]
  );

  return [preferences, updatePreference];
}
